import { useEffect } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useNavigate } from "react-router-dom";
import { Alert } from "@/components/ui/alert";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogTitle,
} from "@/components/ui/dialog";
import { DetailReadout } from "@/modules/shell/DetailReadout";
import { getProduct } from "@/shared/api/inventory";
import { notify } from "@/shared/feedback/notify";
import { RedactedMoney } from "@/shared/feedback/redacted-value";
import { queryKeys } from "@/shared/query/keys";
import { stashListReturn } from "@/modules/shell/list-return-state";
import { productGlanceStats } from "../product-glance-math";

/**
 * Desk side glance — basic info + stats · View more (plan/40).
 * No Edit. Prefetches full detail so View more feels instant.
 */
export function ProductGlancePeek(props: {
  open: boolean;
  productId: string | null;
  companySlug: string;
  listPath: string;
  onOpenChange: (open: boolean) => void;
}) {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const productId = props.productId;

  const detailQuery = useQuery({
    queryKey: queryKeys.products.detail(props.companySlug, productId ?? ""),
    enabled: props.open && Boolean(productId),
    queryFn: () => getProduct(props.companySlug, productId!),
  });

  useEffect(() => {
    if (!props.open || !productId) return;
    void queryClient.prefetchQuery({
      queryKey: queryKeys.products.detail(props.companySlug, productId),
      queryFn: () => getProduct(props.companySlug, productId),
    });
  }, [props.open, productId, props.companySlug, queryClient]);

  const detail = detailQuery.data ?? null;
  const stats = detail ? productGlanceStats(detail) : null;
  const title = detail?.product.name ?? "Product";

  function viewMore() {
    if (!productId) return;
    stashListReturn(props.listPath, productId);
    props.onOpenChange(false);
    navigate(`/c/${props.companySlug}/products/${productId}`);
  }

  async function copySku() {
    const sku = stats?.sku?.trim();
    if (!sku) return;
    try {
      await navigator.clipboard.writeText(sku);
      notify.info(`Copied ${sku}`);
    } catch {
      notify.error("Could not copy SKU");
    }
  }

  return (
    <Dialog open={props.open} onOpenChange={props.onOpenChange}>
      <DialogContent
        className="fixed inset-y-0 right-0 left-auto top-0 z-50 flex h-dvh max-h-dvh w-[min(100%,22.5rem)] max-w-none translate-x-0 translate-y-0 flex-col gap-0 rounded-none border-0 border-l border-[var(--color-border)] p-0 shadow-[var(--shadow-float)]"
        aria-describedby={undefined}
      >
        <div className="flex shrink-0 items-start justify-between gap-2 border-b border-[var(--color-border)] px-4 py-3">
          <div className="min-w-0">
            <DialogTitle className="truncate text-base">{title}</DialogTitle>
            <DialogDescription className="mt-0.5 text-xs text-[var(--color-muted)]">
              Glance · read only
            </DialogDescription>
          </div>
          <Button
            type="button"
            variant="ghost"
            size="sm"
            className="shrink-0"
            onClick={() => props.onOpenChange(false)}
          >
            Close
          </Button>
        </div>

        <div className="min-h-0 flex-1 overflow-auto px-4 py-4">
          {detailQuery.isPending && !detail ? (
            <Alert variant="muted">Loading…</Alert>
          ) : detailQuery.isError ? (
            <Alert variant="destructive">
              {detailQuery.error instanceof Error
                ? detailQuery.error.message
                : "Failed to load"}
            </Alert>
          ) : detail && stats ? (
            <>
              <DetailReadout
                className="mt-0"
                rows={[
                  { label: "Name", value: detail.product.name },
                  { label: "SKU", value: stats.sku, mono: true },
                  { label: "Kind", value: stats.kindLabel },
                  ...(stats.deactivated
                    ? [{ label: "Status", value: "Deactivated" }]
                    : []),
                  ...(detail.product.kind === "stocked"
                    ? [
                        {
                          label: "On hand",
                          value: stats.onHand,
                          mono: true as const,
                        },
                      ]
                    : []),
                  {
                    label: "Avg cost",
                    value: <RedactedMoney value={stats.avgCost} />,
                  },
                  {
                    label: "Price lists",
                    value:
                      stats.priceCount === 0
                        ? "None set"
                        : `${stats.priceCount} list${stats.priceCount === 1 ? "" : "s"}`,
                  },
                ]}
              />
              <div className="mt-6 flex flex-col gap-2">
                <Button type="button" onClick={() => viewMore()}>
                  View more
                </Button>
                {stats.sku ? (
                  <Button
                    type="button"
                    variant="outline"
                    onClick={() => void copySku()}
                  >
                    Copy SKU
                  </Button>
                ) : null}
              </div>
            </>
          ) : null}
        </div>
      </DialogContent>
    </Dialog>
  );
}
